// @flow

import React, { PureComponent } from 'react'
import type { Node } from 'react'
import Helmet from 'react-helmet'
import styled, { ThemeProvider } from 'styled-components'
import theme from 'styled-theming'

import Header from '../components/header'
import ThemeToggle from '../components/theme-toggle'
import { OuterContainer } from '../components/helpers'
import Footer from '../components/footer'
import { WorksNavigation as WorksNav } from '../scenes/works'
import { stylesConfig, myTheme } from '../styles'
import route from '../data/routes'

import './index.css'

const { space, color } = myTheme

// Styles
// ----------------------------------------

const bodyTextColor = theme('mode', {
  light: color.text,
  dark: color.white,
})

const Content = styled(OuterContainer)`
  max-width: ${stylesConfig.contentMaxWidth};
  color: ${bodyTextColor};
`

const ThemeToggleContainer = styled.div`
  position: fixed;
  bottom: ${space.two};
  right: ${space.two};
`

// localStorage key
const UserPreferenceMode = 'user_ui_mode'

// Flow Typing
// ----------------------------------------

type Props = {
  children: Node,
  data: {
    site: {
      siteMetadata: {
        title: string,
        tagline: string,
        keywords: Array<string>,
      },
    },
  },
  location: {
    pathname: string,
  },
}
type State = {
  mode: ?string,
  loading: boolean,
}

class IndexLayout extends PureComponent<Props, State> {
  constructor() {
    super()
    this.state = {
      mode: 'dark',
      loading: true,
    }
  }

  state: State

  componentDidMount() {
    const localUIPreferenceMode = localStorage.getItem(UserPreferenceMode)
    const hasPresetUIPreference = !!localUIPreferenceMode
    const themeMode = hasPresetUIPreference ? localUIPreferenceMode : 'dark'

    // Set localStorage when component mounts
    if (themeMode === null || themeMode === undefined) {
      localStorage.setItem(UserPreferenceMode, 'dark')
    } else {
      localStorage.setItem(UserPreferenceMode, themeMode)
    }

    /* eslint-disable react/no-did-mount-set-state */

    // This triggers a re-render which is unfortunate... but the only way to
    // solve that problem would be to re-write the SSR config and that's just
    // not MVP. ¯\_(ツ)_/¯
    //
    // Known Issues:
    // Since we're not setting the background color of the body until after
    // initial render there's a slight flash if the user preference is set
    // to light mode on intial load or refresh.

    // Set state based on localStorage
    this.setState({
      mode: themeMode,
      loading: false,
    })
    /* eslint-enable */
  }

  handleThemeChange() {
    const switchMode = this.state.mode === 'dark' ? 'light' : 'dark'

    this.setState({ mode: switchMode })
    localStorage.setItem(UserPreferenceMode, switchMode)
  }

  render() {
    const { children, data, location } = this.props
    const { loading } = this.state

    // Destructure data from query
    const { title, tagline, keywords } = data.site.siteMetadata

    const isWorksPage =
      location.pathname === route.erbConnect ||
      location.pathname === route.mavenPro

    // Loading
    if (loading) {
      return null
    }

    const isDarkTheme = this.state.mode === 'dark'
    return (
      <ThemeProvider theme={{ mode: this.state.mode }}>
        <div className="sans-serif">
          <Helmet
            title={title}
            meta={[
              { name: 'description', content: tagline },
              { name: 'keywords', content: keywords },
            ]}
          >
            <html lang="en" />
            <body
              className={
                this.state.mode === 'light' ? 'js-light-theme' : 'js-dark-theme'
              }
            />
          </Helmet>
          <Header
            title={title}
            subtitle={tagline}
          />
          <Content className="pt0 px2 pb3">
            {children()}
            {isWorksPage && <WorksNav disabled />}
          </Content>
          <Footer>
            <ThemeToggleContainer>
              <ThemeToggle
                id="theme-toggle"
                onClick={() => this.handleThemeChange()}
                nightMode={isDarkTheme}
              />
            </ThemeToggleContainer>
          </Footer>
        </div>
      </ThemeProvider>
    )
  }
}

// $FlowFixMe
export const query = graphql`
  query AboutQuery {
    site {
      siteMetadata {
        title
        tagline
        keywords
      }
    }
  }
`

export default IndexLayout



// WEBPACK FOOTER //
// ./src/layouts/index.js